import React, { useState } from 'react';
import Chart from 'react-google-charts';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { options } from './config';
import { Heading, RadioButton, Text } from '../../Elements/Elements';
import { Progress, TrendView, Quantity } from '../parts/parts';
import { monthesPeriods, monthPerLabel } from '../helpers/config/filtersConfig';
import { calcTrend } from '../helpers/config/helpers';

import classes from './AreaEnvironment.module.scss';

const AreaEnvironment = ({ chartData }) => {
  const { t } = useTranslation(['activity']);

  const [licenseUtilPeriod, setLicenseUtilPeriod] = useState('6M');
  const [unit, setUnit] = useState(t('activity:filters.TEAM_MEMBERS'));

  const units = [
    t('activity:filters.TEAM_MEMBERS'),
    t('activity:platform.GATEWAYS')
  ];

  const isPercent = true; // show percent in trend

  let trend = 0;
  const chartArr = [
    [
      'month',
      'Total licenses',
      { type: 'string', role: 'style' },
      'Utilized licenses',
      { type: 'string', role: 'style' }
    ]
  ];
  let totalLicenses = 0;
  let utilLicenses = 0;
  let percentLicenses = 0;
  const length = chartData ? chartData.length : 0;
  let maxChartData = 0;

  const pushData = (cohortMonth, data1 = 0, data2 = 0) => {
    chartArr.push([
      cohortMonth,
      data1,
      'point {  stroke-color: #fff; stroke-width: 1; }',
      data2,
      'point { stroke-color: #fff; stroke-width: 1; }'
    ]);
  };

  const createDataMap = (arr, key1, key2) => {
    arr.map(item => {
      maxChartData = item[key1] > maxChartData ? item[key1] : maxChartData;
      const date = moment(item.cohortMonth).format('MMM');
      pushData(date, item[key1], item[key2]);
      return '';
    });
  };

  const createData = (period, key1, key2) => {
    if (length < period) {
      const date = moment(chartData[0].cohortMonth)
        .add(-1, 'month')
        .format('MMM');
      pushData(date);
      createDataMap(chartData, key1, key2);
    } else {
      const tempArr = chartData.slice(length - period);
      createDataMap(tempArr, key1, key2);
    }
    totalLicenses = chartData[length - 1][key1];
    utilLicenses = chartData[length - 1][key2];
    percentLicenses =
      utilLicenses && totalLicenses
        ? ((utilLicenses / totalLicenses) * 100).toFixed(0)
        : 0;
    if (length === 1) {
      trend = calcTrend(0, utilLicenses);
    } else {
      trend = calcTrend(chartData[length - 2][key2], utilLicenses);
    }
  };

  if (length !== 0) {
    if (unit === units[0]) {
      switch (licenseUtilPeriod) {
        case '1M': {
          createData(2, 'totalTmLicenses', 'totalUsers');
          break;
        }
        case '3M': {
          createData(3, 'totalTmLicenses', 'totalUsers');
          break;
        }
        case '1Y': {
          createData(12, 'totalTmLicenses', 'totalUsers');
          break;
        }
        case '6M':
        default: {
          createData(6, 'totalTmLicenses', 'totalUsers');
          break;
        }
      }
      if (maxChartData >= 10) {
        options.vAxis.maxValue = (maxChartData * 1.2).toFixed(0); //up on 20% if more then 9   // to check!
        if (options.vAxis.maxValue > 1000) options.chartArea.left = 32;
      }
    } else {
      switch (licenseUtilPeriod) {
        case '1M': {
          createData(2, 'totalPsLicenses', 'totalPsUtilized');
          break;
        }
        case '3M': {
          createData(3, 'totalPsLicenses', 'totalPsUtilized');
          break;
        }
        case '1Y': {
          createData(12, 'totalPsLicenses', 'totalPsUtilized');
          break;
        }
        case '6M':
        default: {
          createData(6, 'totalPsLicenses', 'totalPsUtilized');
          break;
        }
      }
      if (maxChartData > 9) {
        options.vAxis.maxValue = (maxChartData * 1.2).toFixed(0); //up on 20% if more then 9   // to check!
      } else {
        options.vAxis.maxValue = 10;
      }
    }
  } else {
    pushData('');
    options.vAxis.maxValue = 10;
    options.pointSize = 0;
  }

  const handleLicenseUtilPeriod = e => {
    if (e.target.value !== licenseUtilPeriod) {
      setLicenseUtilPeriod(e.target.value);
    }
  };

  const handleUnit = e => {
    if (e.target.value !== unit) {
      setUnit(e.target.value);
    }
  };

  return (
    <section className={classes.box}>
      <Heading>{t('activity:customers.LICENSE_UTILIZSTION')}</Heading>
      <div className={classes.info}>
        <Text type="p" className={classes.info_wrap}>
          <Quantity how={utilLicenses} from={totalLicenses} />
          <Progress progress={percentLicenses} />
          <TrendView trend={trend} isPercent={isPercent} />
        </Text>
        <RadioButton
          handleUnit={handleUnit}
          unit={unit}
          units={units}
          name={t('activity:customers.LICENSE_UTILIZSTION_FOR')}
          type="s"
        />
        <RadioButton
          handleUnit={handleLicenseUtilPeriod}
          unit={licenseUtilPeriod}
          units={monthesPeriods}
          name={t('activity:customers.LICENSE_UTILIZSTION_PERIOD')}
          type="c"
          unitsLabel={monthPerLabel}
        />
      </div>
      <div>
        <Chart chartType="AreaChart" options={options} data={chartArr} />
        <div className={classes.legend}>
          <Text className={classes.legend_total}>
            {t('activity:customers.TOTAL_LICENSES')}
          </Text>
          <Text className={classes.legend_util}>
            {t('activity:customers.UTILIZED_LICENSES')}
          </Text>
        </div>
      </div>
    </section>
  );
};

AreaEnvironment.propTypes = {
  chartData: PropTypes.array
};

export default AreaEnvironment;
